// pages/DashboardCuentasPorCobrar.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { obtenerDashboardCuentasPorCobrar, obtenerFacturas } from '../services/facturaService';
import { formatearFecha } from '../utils/dateUtils';
import '../styles/DashboardCuentasPorCobrar.css';
import { useFiscal } from '../hooks/useFiscal';

const DashboardCuentasPorCobrar = () => {
  const fiscal = useFiscal();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [facturasPorVencer, setFacturasPorVencer] = useState([]);
  const [facturasVencidas, setFacturasVencidas] = useState([]);
  const [anticiposPendientes, setAnticiposPendientes] = useState([]);
  const [facturasBorrador, setFacturasBorrador] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      
      // ✅ Cargar dashboard - manejar estructura axios
      const dashRes = await obtenerDashboardCuentasPorCobrar();
      const dataDashboard = dashRes?.data?.data || dashRes?.data || dashRes;
      setDashboard(dataDashboard);

      // ✅ Cargar facturas - manejar estructura axios
      const facturasRes = await obtenerFacturas({});
      const rawFacturas = facturasRes?.data?.data
                        || facturasRes?.data?.facturas
                        || facturasRes?.data
                        || facturasRes;
      const todasFacturas = Array.isArray(rawFacturas) ? rawFacturas
                          : Array.isArray(rawFacturas?.facturas) ? rawFacturas.facturas
                          : Array.isArray(rawFacturas?.rows) ? rawFacturas.rows
                          : [];

      const hoy = new Date();
      const enUnaSemana = new Date();
      enUnaSemana.setDate(hoy.getDate() + 7);

      // Facturas por vencer (próximos 7 días)
      const porVencer = todasFacturas.filter(f => {
        if (!['emitida', 'parcial'].includes(f.estado)) return false;
        const vencimiento = new Date(f.fecha_vencimiento);
        return vencimiento >= hoy && vencimiento <= enUnaSemana;
      });

      // Facturas vencidas: estado 'vencida' O fecha de vencimiento pasada con saldo pendiente
      const vencidas = todasFacturas.filter(f => {
        if (!['emitida', 'parcial', 'vencida'].includes(f.estado)) return false;
        if (f.estado === 'vencida') return true;
        return new Date(f.fecha_vencimiento) < hoy;
      });

      // 🆕 Anticipos emitidos pendientes de cobro
      const anticipos = todasFacturas.filter(f =>
        f.tipo === 'anticipo' && ['emitida', 'parcial'].includes(f.estado)
      );

      // 🆕 Facturas en borrador (pendientes de emitir)
      const borradores = todasFacturas.filter(f => f.estado === 'borrador');

      setFacturasPorVencer(porVencer);
      setFacturasVencidas(vencidas);
      setAnticiposPendientes(anticipos);
      setFacturasBorrador(borradores);

    } catch (error) {
      console.error('Error cargando dashboard:', error);
      alert('Error al cargar dashboard de cuentas por cobrar');
    } finally {
      setLoading(false);
    }
  };

  const formatearMonto = (monto) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: fiscal.moneda?.codigo || 'DOP'
    }).format(monto);
  };

  const calcularPorcentajeCobrado = () => {
    if (!dashboard) return 0;
    const totalFacturado = dashboard.totalFacturado || (dashboard.totalPorCobrar + dashboard.totalCobrado);
    if (totalFacturado === 0) return 0;
    return ((dashboard.totalCobrado / totalFacturado) * 100).toFixed(1);
  };

  const getDiasVencimiento = (fechaVencimiento) => {
    const hoy = new Date();
    const vencimiento = new Date(fechaVencimiento);
    const diffTime = vencimiento - hoy;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="dashboard-cxc-container">
        <div className="loading">Cargando dashboard...</div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="dashboard-cxc-container">
        <div className="error">Error al cargar datos</div>
      </div>
    );
  }

  return (
    <div className="dashboard-cxc-container">

      {/* ── HEADER COMPACTO ── */}
      <div className="dashboard-cxc-header">
      <div className="header-left">
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
    <span>📊</span>
    <h1>Cuentas por Cobrar</h1>
  </div>
  <span className="subtitle">Resumen ejecutivo de facturación y cobranza</span>
</div>
        <div className="header-right">
          <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>
            {new Date().toLocaleDateString('es-DO', { hour: '2-digit', minute: '2-digit' })}
          </span>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/facturas')}
          >
            ← Facturas
          </button>
        </div>
      </div>

      {/* ── CONTENIDO ── */}
      <div className="dashboard-cxc-content">

      {/* KPIs Principales */}
      <div className="kpis-grid">
        <div className="kpi-card primary">
          <div className="kpi-icon">💰</div>
          <div className="kpi-content">
            <p className="kpi-label">Total por Cobrar</p>
            <h2 className="kpi-value">{formatearMonto(dashboard.totalPorCobrar || 0)}</h2>
            <p className="kpi-detail">{dashboard.facturasPendientes || 0} facturas pendientes</p>
          </div>
        </div>

        <div className="kpi-card danger">
          <div className="kpi-icon">⚠️</div>
          <div className="kpi-content">
            <p className="kpi-label">Facturas Vencidas</p>
            <h2 className="kpi-value">{formatearMonto(dashboard.totalVencido || 0)}</h2>
            <p className="kpi-detail">{dashboard.facturasVencidas || 0} facturas vencidas</p>
          </div>
        </div>

        <div className="kpi-card warning">
          <div className="kpi-icon">⏰</div>
          <div className="kpi-content">
            <p className="kpi-label">Por Vencer (7 días)</p>
            <h2 className="kpi-value">{formatearMonto(dashboard.totalPorVencer || 0)}</h2>
            <p className="kpi-detail">{dashboard.facturasPorVencer || 0} facturas próximas</p>
          </div>
        </div>

        <div className="kpi-card success">
          <div className="kpi-icon">✅</div>
          <div className="kpi-content">
            <p className="kpi-label">Tasa de Cobro</p>
            <h2 className="kpi-value">{calcularPorcentajeCobrado()}%</h2>
            <p className="kpi-detail">Cobrado vs Facturado</p>
          </div>
        </div>

        {/* 🆕 Anticipos pendientes */}
        <div className="kpi-card" style={{ borderLeft: '4px solid #8b5cf6' }}>
          <div className="kpi-icon">🤝</div>
          <div className="kpi-content">
            <p className="kpi-label">Anticipos Pendientes</p>
            <h2 className="kpi-value">{formatearMonto(
              anticiposPendientes.reduce((sum, f) => sum + parseFloat(f.total || 0), 0)
            )}</h2>
            <p className="kpi-detail">{anticiposPendientes.length} anticipos emitidos</p>
          </div>
        </div>

        {/* 🆕 Borradores */}
        <div className="kpi-card" style={{ borderLeft: '4px solid #94a3b8' }}>
          <div className="kpi-icon">📝</div>
          <div className="kpi-content">
            <p className="kpi-label">En Borrador</p>
            <h2 className="kpi-value">{facturasBorrador.length}</h2>
            <p className="kpi-detail">Facturas sin emitir</p>
          </div>
        </div>
      </div>

      {/* Gráfico de barras simple */}
      <div className="charts-grid">
        <div className="chart-card">
          <h3 className="chart-title">Estado de Cobranza</h3>
          <div className="bar-chart">
            <div className="bar-item">
              <div className="bar-info">
                <span className="bar-label">Por Cobrar</span>
                <span className="bar-value">{formatearMonto(dashboard.totalPorCobrar)}</span>
              </div>
              <div className="bar-container">
                <div 
                  className="bar-fill primary"
                  style={{
                    width: `${dashboard.totalFacturado > 0 ? (dashboard.totalCobrado / dashboard.totalFacturado * 100) : 0}%`
                  }}
                />
              </div>
            </div>
            <div className="bar-item">
              <div className="bar-info">
                <span className="bar-label">Vencido</span>
                <span className="bar-value">{formatearMonto(dashboard.totalVencido)}</span>
              </div>
              <div className="bar-container">
                <div
                  className="bar-fill danger"
                  style={{
                    width: `${dashboard.totalFacturado > 0 ? (dashboard.totalVencido / dashboard.totalFacturado * 100) : 0}%`
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="chart-card">
          <h3 className="chart-title">Resumen por Estado</h3>
          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-dot primary"></span>
              <span className="stat-label">Emitidas</span>
              <span className="stat-value">{dashboard.facturasPendientes}</span>
            </div>
            <div className="stat-item">
              <span className="stat-dot warning"></span>
              <span className="stat-label">Próximas a vencer</span>
              <span className="stat-value">{facturasPorVencer.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-dot danger"></span>
              <span className="stat-label">Vencidas</span>
              <span className="stat-value">{facturasVencidas.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Facturas Vencidas */}
      {facturasVencidas.length > 0 && (
        <div className="table-card danger-card">
          <div className="table-header">
            <h3>⚠️ Facturas Vencidas</h3>
            <span className="badge badge-danger">{facturasVencidas.length}</span>
          </div>
          <div className="table-container">
            <table className="facturas-table">
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Cliente</th>
                  <th>Fecha Venc.</th>
                  <th>Días Vencido</th>
                  <th className="text-right">Saldo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {facturasVencidas.slice(0, 10).map((factura) => (
                  <tr key={factura.id}>
                    <td className="font-bold">{factura.numero_factura}</td>
                    <td>{factura.cliente?.nombre}</td>
                    <td>{formatearFecha(factura.fecha_vencimiento)}</td>
                    <td>
                      <span className="badge badge-danger">
                        {Math.abs(getDiasVencimiento(factura.fecha_vencimiento))} días
                      </span>
                    </td>
                    <td className="text-right font-bold text-danger">
                      {formatearMonto(factura.saldoPendiente ?? factura.total)}
                    </td>
                    <td>
                      <button 
                        className="btn-icon"
                        onClick={() => navigate(`/facturas/${factura.id}`)}
                      >
                        👁️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Facturas por Vencer */}
      {facturasPorVencer.length > 0 && (
        <div className="table-card warning-card">
          <div className="table-header">
            <h3>⏰ Facturas por Vencer (Próximos 7 días)</h3>
            <span className="badge badge-warning">{facturasPorVencer.length}</span>
          </div>
          <div className="table-container">
            <table className="facturas-table">
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Cliente</th>
                  <th>Fecha Venc.</th>
                  <th>Días Restantes</th>
                  <th className="text-right">Saldo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {facturasPorVencer.map((factura) => (
                  <tr key={factura.id}>
                    <td className="font-bold">{factura.numero_factura}</td>
                    <td>{factura.cliente?.nombre}</td>
                    <td>{formatearFecha(factura.fecha_vencimiento)}</td>
                    <td>
                      <span className="badge badge-warning">
                        {getDiasVencimiento(factura.fecha_vencimiento)} días
                      </span>
                    </td>
                    <td className="text-right font-bold text-warning">
                      {formatearMonto(factura.saldoPendiente ?? factura.total)}
                    </td>
                    <td>
                      <button 
                        className="btn-icon"
                        onClick={() => navigate(`/facturas/${factura.id}`)}
                      >
                        👁️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 🆕 Anticipos Pendientes de Cobro */}
      {anticiposPendientes.length > 0 && (
        <div className="table-card" style={{ borderTop: '3px solid #8b5cf6' }}>
          <div className="table-header">
            <h3>🤝 Anticipos Pendientes de Cobro</h3>
            <span className="badge" style={{ background: '#8b5cf6', color: 'white' }}>
              {anticiposPendientes.length}
            </span>
          </div>
          <div className="table-container">
            <table className="facturas-table">
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Cliente</th>
                  <th>Proyecto</th>
                  <th>NCF</th>
                  <th>Fecha Emisión</th>
                  <th className="text-right">Total</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {anticiposPendientes.map((factura) => (
                  <tr key={factura.id}>
                    <td className="font-bold">{factura.numero_factura}</td>
                    <td>{factura.Cliente?.nombre || factura.cliente?.nombre || '-'}</td>
                    <td>{factura.Proyecto?.nombre || factura.proyecto?.nombre || '-'}</td>
                    <td>
                      {factura.ncf
                        ? <span className="badge" style={{ background: '#d1fae5', color: '#065f46' }}>{factura.ncf}</span>
                        : <span className="badge" style={{ background: '#fee2e2', color: '#991b1b' }}>Sin NCF</span>
                      }
                    </td>
                    <td>{formatearFecha(factura.fecha_emision)}</td>
                    <td className="text-right font-bold" style={{ color: '#8b5cf6' }}>
                      {formatearMonto(factura.total)}
                    </td>
                    <td>
                      <button
                        className="btn-icon"
                        onClick={() => navigate(`/facturas/${factura.id}`)}
                      >
                        👁️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 🆕 Facturas en Borrador */}
      {facturasBorrador.length > 0 && (
        <div className="table-card" style={{ borderTop: '3px solid #94a3b8' }}>
          <div className="table-header">
            <h3>📝 Facturas en Borrador (Pendientes de Emitir)</h3>
            <span className="badge" style={{ background: '#94a3b8', color: 'white' }}>
              {facturasBorrador.length}
            </span>
          </div>
          <div className="table-container">
            <table className="facturas-table">
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Cliente</th>
                  <th>Tipo</th>
                  <th>Fecha Emisión</th>
                  <th className="text-right">Total</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {facturasBorrador.slice(0, 10).map((factura) => (
                  <tr key={factura.id}>
                    <td className="font-bold">{factura.numero_factura || `#${factura.id}`}</td>
                    <td>{factura.Cliente?.nombre || factura.cliente?.nombre || '-'}</td>
                    <td>
                      <span className="badge" style={{
                        background: factura.tipo === 'anticipo' ? '#ede9fe' : '#f0f9ff',
                        color: factura.tipo === 'anticipo' ? '#7c3aed' : '#0369a1'
                      }}>
                        {factura.tipo === 'anticipo' ? '🤝 Anticipo' : factura.tipo === 'certificacion' ? '📋 Certificación' : '📄 Normal'}
                      </span>
                    </td>
                    <td>{formatearFecha(factura.fecha_emision)}</td>
                    <td className="text-right font-bold">
                      {formatearMonto(factura.total)}
                    </td>
                    <td>
                      <button
                        className="btn-icon"
                        onClick={() => navigate(`/facturas/${factura.id}`)}
                      >
                        👁️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Mensaje si no hay facturas pendientes */}
      {facturasVencidas.length === 0 && facturasPorVencer.length === 0 &&
       anticiposPendientes.length === 0 && facturasBorrador.length === 0 && (
        <div className="empty-state">
          <p className="empty-icon">✅</p>
          <h3>¡Todo al día!</h3>
          <p>No hay facturas pendientes de cobro</p>
        </div>
      )}

      </div>{/* ── FIN CONTENIDO ── */}

      {/* ── FOOTER ── */}
      <div className="dashboard-cxc-footer">
        <span>💼 Sistema de Gestión Comercial</span>
        <span>© {new Date().getFullYear()} • Cuentas por Cobrar v1.0</span>
      </div>

    </div>
  );
};

export default DashboardCuentasPorCobrar;